import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type NavigateOptions = { replace?: boolean };
type Navigate = (to: string, options?: NavigateOptions) => void;
type Params = Record<string, string>;

const RouteParamsContext = createContext<Params>({});
const NAVIGATION_EVENT = "linguahome:navigation";

function currentLocation() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function notifyNavigation() {
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}

export function navigate(to: string, options: NavigateOptions = {}) {
  if (options.replace) window.history.replaceState({}, "", to);
  else window.history.pushState({}, "", to);
  notifyNavigation();
}

export function useLocation(): [string, Navigate] {
  const [location, setLocation] = useState(currentLocation);
  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener("popstate", update);
    window.addEventListener(NAVIGATION_EVENT, update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(NAVIGATION_EVENT, update);
    };
  }, []);
  const go = useCallback<Navigate>((to, options) => navigate(to, options), []);
  return [location, go];
}

function matchPath(pattern: string, pathname = window.location.pathname): Params | null {
  if (pattern === "/") return pathname === "/" ? {} : null;
  const patternSegments = pattern.replace(/^\/+|\/+$/g, "").split("/");
  const pathSegments = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (patternSegments.length !== pathSegments.length) return null;
  const params: Params = {};
  for (let index = 0; index < patternSegments.length; index++) {
    const expected = patternSegments[index];
    const actual = pathSegments[index];
    if (expected.startsWith(":")) params[expected.slice(1)] = decodeURIComponent(actual);
    else if (expected !== actual) return null;
  }
  return params;
}

export function useRoute(pattern: string): [boolean, Params | null] {
  const [location] = useLocation();
  return useMemo(() => {
    const pathname = location.split(/[?#]/)[0];
    const params = matchPath(pattern, pathname);
    return [Boolean(params), params];
  }, [location, pattern]);
}

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
}

export function Link({ href, onClick, target, children, ...props }: LinkProps) {
  return <a
    {...props}
    href={href}
    target={target}
    onClick={(event) => {
      onClick?.(event);
      if (event.defaultPrevented || target === "_blank" || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (/^(https?:|mailto:|tel:)/i.test(href)) return;
      event.preventDefault();
      navigate(href);
    }}
  >{children}</a>;
}

interface RouteProps {
  path?: string;
  children: React.ReactNode;
}

export function Route({ path, children }: RouteProps) {
  const [location] = useLocation();
  const pathname = location.split(/[?#]/)[0];
  const inherited = useContext(RouteParamsContext);
  const params = path ? matchPath(path, pathname) : inherited;
  if (path && !params) return null;
  return <RouteParamsContext.Provider value={params || {}}>{children}</RouteParamsContext.Provider>;
}

export function Switch({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const pathname = location.split(/[?#]/)[0];
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement<RouteProps>(child)) continue;
    const path = child.props.path;
    const params = path ? matchPath(path, pathname) : {};
    if (params) return <RouteParamsContext.Provider value={params}>{child}</RouteParamsContext.Provider>;
  }
  return null;
}

export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}
